import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  getPatterns,
  getMeasurements,
  getGarmentSpecificationFields,
  getGarmentTypeConfigs,
  getMuslinMatchRecommendations,
  recordMuslinDecision,
} from '@/lib/api';
import { Link2, ExternalLink, Ruler, FileText, SearchCheck } from 'lucide-react';
import { MuslinMatchPanel } from '@/components/muslin/MuslinMatchPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatGarmentTypeLabel } from '@/lib/displayUtils';
import { buildMuslinTargetDesignProfile, formatMuslinDesignValue, getMuslinDesignConfig } from '@/lib/muslinDesign';
import { getBodyMeasurementValue } from '@/lib/bodyMeasurementFallbacks';

const GARMENT_TYPES = {
  jacket: { prefix: 'J', color: 'bg-blue-500' },
  trouser: { prefix: 'T', color: 'bg-green-500' },
  shirt: { prefix: 'S', color: 'bg-purple-500' },
  waistcoat: { prefix: 'W', color: 'bg-amber-500' },
  overcoat: { prefix: 'O', color: 'bg-slate-500' },
};

const BODY_MEASUREMENT_FIELDS = {
  jacket: ['chest_circumference', 'upper_waist_circumference', 'belly_circumference', 'shoulder', 'sleeve_length'],
  trouser: ['lower_waist_circumference', 'lower_seat_hip_circumference', 'thigh_circumference', 'outseam', 'inseam'],
  shirt: ['chest_circumference', 'upper_waist_circumference', 'neck_circumference', 'sleeve_length', 'wrist_circumference'],
  waistcoat: ['chest_circumference', 'upper_waist_circumference', 'belly_circumference', 'front_length', 'back_length'],
  overcoat: ['chest_circumference', 'upper_waist_circumference', 'belly_circumference', 'shoulder', 'coat_length'],
};

const MEASUREMENT_LABELS = {
  chest_circumference: 'Chest',
  upper_waist_circumference: 'Waist',
  belly_circumference: 'Belly',
  shoulder: 'Shoulder',
  sleeve_length: 'Sleeve',
  lower_waist_circumference: 'Lower Waist',
  lower_seat_hip_circumference: 'Seat / Hip',
  thigh_circumference: 'Thigh',
  outseam: 'Outseam',
  inseam: 'Inseam',
  neck_circumference: 'Neck',
  wrist_circumference: 'Wrist',
  front_length: 'Front Length',
  back_length: 'Back Length',
  coat_length: 'Coat Length',
};

const normalizeGarmentTypeKey = (value) => String(value || '').replace(/^test_/, '').toLowerCase();
const getDesignEntries = (garmentType, designProfile = {}) => {
  return getMuslinDesignConfig(normalizeGarmentTypeKey(garmentType))
    .map((entry) => ({ ...entry, value: formatMuslinDesignValue(designProfile?.[entry.key]) }))
    .filter((entry) => entry.value);
};

const getBodyMeasurementSummary = (garmentType, measurement) => (BODY_MEASUREMENT_FIELDS[normalizeGarmentTypeKey(garmentType)] || [])
  .map((field) => ({ key: field, label: MEASUREMENT_LABELS[field] || field.replace(/_/g, ' '), value: measurement?.[field] ?? null }))
  .filter((item) => item.value !== null && item.value !== undefined && item.value !== '');

const PatternSelectorModal = ({ isOpen, onClose, customerId, garmentType, garment = null, sourceCustomizations = [], onSelectPattern }) => {
  const [patterns, setPatterns] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [specFields, setSpecFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatternId, setSelectedPatternId] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [savingPattern, setSavingPattern] = useState(false);
  const [showPatternPicker, setShowPatternPicker] = useState(false);

  const rawGarmentType = String(garmentType || '').toLowerCase();
  const normalizedGarmentType = normalizeGarmentTypeKey(garmentType);
  const isTestGarment = Boolean(garment?.is_test_garment) || rawGarmentType.startsWith('test_') || rawGarmentType.startsWith('test ');
  const showCloseMatchActions = isTestGarment && Boolean(garment?.garment_id);

  useEffect(() => {
    if (isOpen && customerId && garmentType) {
      fetchData();
    }
  }, [isOpen, customerId, garmentType]);

  useEffect(() => {
    if (isOpen) {
      setShowPatternPicker(!isTestGarment);
    }
  }, [isOpen, isTestGarment]);

  useEffect(() => {
    if (!isOpen) {
      setMatchData(null);
      setMatchLoading(false);
      setSavingPattern(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const patternsRes = await getPatterns(customerId, normalizedGarmentType);
      const sortedPatterns = [...patternsRes.data].sort((a, b) => {
        const getNumber = (code) => {
          const parts = String(code || '').split('-');
          return parseInt(parts[parts.length - 1], 10) || 0;
        };
        return getNumber(b.pattern_code) - getNumber(a.pattern_code);
      });
      setPatterns(sortedPatterns);

      const measurementsRes = await getMeasurements(customerId);
      setMeasurements(measurementsRes.data || []);

      const configsRes = await getGarmentTypeConfigs();
      const config = (configsRes.data || []).find((entry) => entry.garment_type === normalizedGarmentType);
      if (config) {
        const fieldsRes = await getGarmentSpecificationFields(config.config_id);
        setSpecFields(fieldsRes.data || []);
      } else {
        setSpecFields([]);
      }

      setSelectedPatternId(sortedPatterns[0]?.pattern_id || null);
    } catch (error) {
      console.error('Failed to fetch patterns:', error);
      toast.error('Failed to load patterns');
    } finally {
      setLoading(false);
    }
  };

  const selectedPattern = patterns.find((pattern) => pattern.pattern_id === selectedPatternId);
  const activePattern = (!isTestGarment || showPatternPicker) ? selectedPattern : null;
  const linkedMeasurement = activePattern?.linked_measurement_id
    ? measurements.find((measurement) => measurement.measurement_id === activePattern.linked_measurement_id)
    : measurements[0];

  const currentDesignProfile = useMemo(() => {
    const localProfile = buildMuslinTargetDesignProfile(sourceCustomizations, garmentType);
    return Object.keys(localProfile).length > 0 ? localProfile : (matchData?.target_design_profile || {});
  }, [sourceCustomizations, garmentType, matchData]);

  const currentDesignEntries = useMemo(() => getDesignEntries(garmentType, currentDesignProfile), [garmentType, currentDesignProfile]);
  const bodyMeasurementSummary = useMemo(() => getBodyMeasurementSummary(garmentType, linkedMeasurement), [garmentType, linkedMeasurement]);
  const garmentInfo = GARMENT_TYPES[normalizedGarmentType] || { prefix: 'G', color: 'bg-gray-500' };
  const garmentLabel = formatGarmentTypeLabel(garmentType);
  const showBriefSummaryCard = isTestGarment && !showPatternPicker;

  const handleFindCloseMatch = async () => {
    if (!garment?.garment_id) {
      toast.error('This garment is missing match context');
      return;
    }
    setMatchLoading(true);
    try {
      const response = await getMuslinMatchRecommendations(garment.garment_id);
      setMatchData(response.data);
    } catch (error) {
      console.error('Failed to fetch muslin matches:', error);
      toast.error('Failed to load muslin recommendations');
    } finally {
      setMatchLoading(false);
    }
  };

  const handleReuseCandidate = async (candidate) => {
    try {
      await recordMuslinDecision({
        garment_id: garment.garment_id,
        decision: 'reuse_existing',
        muslin_inventory_id: candidate.muslin_inventory_id,
        notes: `Reserved from close-match suggestion (${candidate.confidence} ${candidate.score})`,
      });
      if (candidate.linked_pattern_code && onSelectPattern) {
        const saved = await onSelectPattern(candidate.linked_pattern_code);
        if (saved === false) {
          toast.error('Muslin reserved, but the pattern number still needs to be saved manually.');
          await handleFindCloseMatch();
          return;
        }
      }
      toast.success(`Reserved ${candidate.reference_code}`);
      onClose();
    } catch (error) {
      console.error('Failed to reserve muslin candidate:', error);
      toast.error('Failed to reserve this muslin');
    }
  };

  const handleBuildNewMuslin = () => {
    setShowPatternPicker(true);
  };

  const handleSaveSelectedPattern = async () => {
    if (!selectedPattern?.pattern_code) {
      toast.error('Choose a pattern first');
      return;
    }

    try {
      setSavingPattern(true);
      const saved = await onSelectPattern?.(selectedPattern.pattern_code);
      if (saved === false) {
        return;
      }
      onClose();
    } catch (error) {
      console.error('Failed to save selected pattern:', error);
      toast.error('Failed to save the pattern number');
    } finally {
      setSavingPattern(false);
    }
  };

  const renderPatternSelection = (title) => {
    if (patterns.length === 0) {
      return (
        <div className="space-y-3" data-testid="pattern-selector-empty-state">
          <div className="flex items-start gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-3 py-3">
            <FileText className="mt-0.5 h-5 w-5 text-muted-foreground opacity-70" />
            <div>
              <p className="text-sm font-medium text-slate-900">No saved {formatGarmentTypeLabel(normalizedGarmentType).toLowerCase()} patterns for this client yet</p>
              <p className="mt-1 text-xs text-slate-600">Create or update the client pattern in Fitting Studio, then return here to save the pattern no.</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => window.open(`/fitting-studio/${customerId}`, '_blank', 'noopener,noreferrer')} data-testid="pattern-selector-empty-open-studio-button">
            <ExternalLink className="mr-2 h-4 w-4" />
            Go to Fitting Studio
          </Button>
        </div>
      );
    }

    try {
      return (
        <div className="space-y-3" data-testid="pattern-selector-save-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="text-sm font-medium">{title}</label>
            {garment?.pattern_no && (
              <Badge variant="outline" data-testid="pattern-selector-current-pattern-badge">
                Current: {garment.pattern_no}
              </Badge>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select value={selectedPatternId || ''} onValueChange={setSelectedPatternId}>
              <SelectTrigger className="flex-1" data-testid="pattern-selector-pattern-trigger">
                <SelectValue placeholder="Choose a pattern..." />
              </SelectTrigger>
              <SelectContent data-testid="pattern-selector-pattern-content">
                {patterns.map((pattern) => (
                  <SelectItem key={pattern.pattern_id} value={pattern.pattern_id} data-testid={`pattern-selector-pattern-option-${pattern.pattern_id}`}>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{pattern.pattern_code}</Badge>
                      {pattern.notes && <span className="text-xs text-muted-foreground">- {pattern.notes}</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSaveSelectedPattern} disabled={!selectedPatternId || savingPattern} data-testid="pattern-selector-save-pattern-button">
              {savingPattern ? 'Saving...' : 'Save Pattern No'}
            </Button>
          </div>
        </div>
      );
    } catch (error) {
      console.error('Failed to render pattern selection:', error);
      return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="flex h-[94vh] w-[96vw] max-w-[1820px] flex-col overflow-hidden p-0"
        onInteractOutside={(event) => event.preventDefault()}
        data-testid="pattern-selector-modal"
      >
        <DialogHeader className="border-b border-slate-200 px-6 py-4 pr-14">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className={`flex h-8 w-8 items-center justify-center rounded ${garmentInfo.color} text-sm font-bold text-white`}>
              {garmentInfo.prefix}
            </div>
            Select Pattern - {garmentLabel}
          </DialogTitle>
          <DialogDescription>
            {isTestGarment
              ? 'For test garments, either find a close muslin match or build a new muslin using one of the client’s saved patterns.'
              : 'Select one of the client’s saved patterns, review the measurements, and save the pattern no to this garment.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          {loading ? (
            <div className="space-y-3 px-6 py-6">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-72 w-full" />
            </div>
          ) : (
            <div className={`grid min-h-0 flex-1 gap-0 ${showCloseMatchActions ? 'lg:grid-cols-[minmax(390px,0.82fr)_minmax(520px,1.18fr)]' : 'grid-cols-1'}`}>
              <div className="min-h-0 overflow-y-auto border-b border-slate-200 px-5 py-4 lg:border-b-0 lg:border-r">
                <div className="flex h-full min-h-0 flex-col gap-3">
                  <Card>
                    <CardContent className="space-y-3 px-4 py-3">
                      {showCloseMatchActions ? (
                        <>
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-slate-900">Choose the next step</p>
                              <p className="mt-1 text-xs text-slate-600">Find a close match to reuse an existing muslin, or build a new muslin by saving one of this client’s patterns.</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button variant="outline" onClick={handleFindCloseMatch} className="gap-2" data-testid="pattern-selector-find-close-match-button">
                                <SearchCheck className="h-4 w-4" />
                                Find Close Match
                              </Button>
                              <Button variant="outline" onClick={handleBuildNewMuslin} data-testid="pattern-selector-build-new-muslin-button">
                                Build New Muslin
                              </Button>
                            </div>
                          </div>
                          {showPatternPicker ? renderPatternSelection('Select Pattern for New Muslin') : null}
                        </>
                      ) : (
                        renderPatternSelection('Select Pattern')
                      )}
                    </CardContent>
                  </Card>

                  {showBriefSummaryCard && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Client Body Measurements</CardTitle>
                        <CardDescription>Key body measurements and design requirements for the close-match decision.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid gap-2 sm:grid-cols-2" data-testid="pattern-selector-body-measurements-card">
                          {bodyMeasurementSummary.length > 0 ? bodyMeasurementSummary.map((item) => (
                            <div key={item.key} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                              <div className="text-[11px] uppercase tracking-wide text-slate-500">{item.label}</div>
                              <div className="mt-1 font-semibold text-slate-900">{item.value} {linkedMeasurement?.unit || 'in'}</div>
                            </div>
                          )) : (
                            <p className="text-sm text-slate-500">No client body measurements found.</p>
                          )}
                        </div>

                        {currentDesignEntries.length > 0 && (
                          <div>
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Current garment design requirements</div>
                            <div className="flex flex-wrap gap-2">
                              {currentDesignEntries.map((entry) => (
                                <span key={entry.key} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-700">
                                  {entry.label}: {entry.value}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {activePattern && activePattern.garment_specifications && (
                    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
                      <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-sm">
                          <Ruler className="h-4 w-4 text-amber-500" />
                          Pattern Measurements - {activePattern.pattern_code}
                        </CardTitle>
                        <CardDescription>Review the linked body measurement against the selected pattern measurement set.</CardDescription>
                      </CardHeader>
                      <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        {Object.keys(activePattern.garment_specifications).length === 0 ? (
                          <p className="py-4 text-center text-sm text-muted-foreground">No measurements configured for this pattern.</p>
                        ) : (
                          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                            <div className="grid grid-cols-3 gap-4 rounded-lg bg-muted/50 px-3 py-2 text-xs font-medium">
                              <div>Specification</div>
                              <div className="text-center">Body Measurement</div>
                              <div className="text-center">Garment Measurement</div>
                            </div>
                            <div className="space-y-1">
                              {Object.entries(activePattern.garment_specifications).map(([key, value]) => {
                                const specField = specFields.find((field) => field.field_key === key);
                                const fieldLabel = specField?.field_label || key.replace(/_/g, ' ');
                                const linkedFieldKey = specField?.linked_measurement_field;
                                const bodyValue = linkedMeasurement && linkedFieldKey
                                  ? getBodyMeasurementValue(linkedMeasurement, linkedFieldKey)
                                  : null;
                                const unit = linkedMeasurement?.unit || 'in';
                                return (
                                  <div key={key} className="grid grid-cols-3 items-center gap-4 rounded-lg border border-border/40 bg-white px-3 py-2 text-sm">
                                    <div className="flex items-center gap-2">
                                      <span>{fieldLabel}</span>
                                      {bodyValue !== null && bodyValue !== undefined && <Link2 className="h-3 w-3 text-amber-500" />}
                                    </div>
                                    <div className="text-center">
                                      {bodyValue !== null && bodyValue !== undefined ? (
                                        <span className="rounded bg-muted px-2 py-1 text-sm text-muted-foreground">{bodyValue} {unit}</span>
                                      ) : (
                                        <span className="text-sm text-muted-foreground">—</span>
                                      )}
                                    </div>
                                    <div className="text-center"><span className="text-sm font-medium">{value} {unit}</span></div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              {showCloseMatchActions ? (
                <div className="flex min-h-0 flex-col overflow-hidden bg-slate-50/40 px-5 py-4">
                  <MuslinMatchPanel
                    loading={matchLoading}
                    data={matchData}
                    onReuseCandidate={handleReuseCandidate}
                    garmentType={garmentType}
                    targetDesignProfile={currentDesignProfile}
                  />
                </div>
              ) : (
                <div className="min-h-0 overflow-y-auto bg-slate-50/40 px-5 py-4">
                  <Card className="border border-dashed border-slate-300 bg-white/80">
                    <CardHeader>
                      <CardTitle className="text-base">Pattern registration</CardTitle>
                      <CardDescription>Actual garments do not use close-match muslin suggestions here. If a new pattern version is needed, create it in Fitting Studio from All Clients, then come back and save that pattern no to this garment.</CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              )}
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatternSelectorModal;